import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertCircle, CheckCircle2, Undo2, X } from "lucide-react";

export interface ToastOptions {
  message: string;
  tone?: "neutral" | "success" | "error";
  actionLabel?: string;
  onAction?: () => void;
  /** ms. Default 5500 (dentro da janela de 5-6s pedida no wireframe). */
  durationMs?: number;
}

interface ActiveToast extends ToastOptions {
  id: number;
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      const toast: ActiveToast = { id, durationMs: 5500, ...options };
      setToasts((current) => [...current, toast]);
      window.setTimeout(() => dismiss(id), toast.durationMs);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((toast) => {
          const tone = toast.tone ?? "neutral";
          return (
            <div key={toast.id} className={`toast toast--${tone}`}>
              {tone === "success" && <CheckCircle2 className="icon" size={18} aria-hidden="true" />}
              {tone === "error" && <AlertCircle className="icon" size={18} aria-hidden="true" />}
              <span>{toast.message}</span>
              {toast.actionLabel && toast.onAction && (
                <button
                  type="button"
                  className="toast__action"
                  onClick={() => {
                    toast.onAction?.();
                    dismiss(toast.id);
                  }}
                >
                  <Undo2 className="icon" size={14} aria-hidden="true" />
                  {toast.actionLabel}
                </button>
              )}
              <button
                type="button"
                className="toast__close"
                aria-label="Fechar aviso"
                onClick={() => dismiss(toast.id)}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast precisa ser usado dentro de <ToastProvider>.");
  }
  return ctx;
}
